/**
 * RoleCompetencies — role-level competency planning screen.
 *
 * Allows users with the `role:model:self` permission to view and configure
 * the competencies mapped to their own role. The component operates in two
 * modes controlled by the `isConfigureMode` flag:
 *
 * - View mode   : read-only display of competencies, relevance levels, and
 *                 expected performance levels.
 * - Configure mode: adds/removes competency assignments for the role and
 *                   sets per-competency relevance and performance targets.
 *
 * Data is split across several Supabase tables — see schema reference below.
 */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './roleCompetencies.css';

// ── Schema reference ────────────────────────────────────────────────────────
// department                  : department_id, depart_name
// roles                       : role_id, role_title, department_id
// rolecompetencies            : role_id, competency_id
// competencies                : competency_id, competency_name, group_id,
//                               department_id, role_id, display_order
// competency_groups           : group_id, group_name, group_type, category_id,
//                               department_id, role_id, display_order
// competency_descriptions     : description_id, competency_id, role_id,
//                               description_level (1-4), description_name
// competency_items            : item_id, competency_id, description, display_order
// competency_relevance_levels : relevance_id, role_id, competency_id,
//                               level_1_relevance … level_4_relevance (char A-F)
// competencyrelevanceperformance : competency_id, relevance_id, performance_id
// expectedperformancelevels   : performance_level (PK int), description
// relevancelevels             : relevance_id (PK), relevance_level (char), relevance_description
// level_descriptions          : level_id (PK), level_number (1-4), title, description
// ───────────────────────────────────────────────────────────────────────────

const relKey = (lv) => `level_${lv}_relevance`;
const emptyRelRow = () => ({
  level_1_relevance: '', level_2_relevance: '',
  level_3_relevance: '', level_4_relevance: '',
});
const emptyPerfRow = () => ({ lv1: '', lv2: '', lv3: '', lv4: '' });

// Static fallbacks used while DB data loads
const FALLBACK_LEVEL_LABELS = [
  'Entry Level', 'Operational Excellence', 'Functional Accountability', 'Growth and Sustainability',
];
const FALLBACK_RELEVANCE_OPTIONS = [
  { value: 'A', label: 'Not Necessary' },
  { value: 'B', label: 'Nice to Have' },
  { value: 'C', label: 'Expected' },
  { value: 'D', label: 'Necessary' },
  { value: 'E', label: 'Mission-Critical' },
];
const FALLBACK_PERF_OPTIONS = [
  { value: 1, label: 'Basic' }, { value: 2, label: 'Above Basic' },
  { value: 3, label: 'Acceptable' }, { value: 4, label: 'Very Good' }, { value: 5, label: 'Expert' },
];


// ── Descriptor accordion ────────────────────────────────────────────────────
const DescriptorAccordion = ({ competencyId, descriptions, items, levelLabels }) => {
  const [open, setOpen] = useState(false);
  const levelDesc = descriptions.filter(d => d.competency_id === competencyId);
  const compItems = (items || []).filter(i => i.competency_id === competencyId);
  if (!levelDesc.length && !compItems.length) return null;

  return (
    <div className="rc-desc-accordion">
      <button className="rc-desc-toggle" onClick={() => setOpen(o => !o)}>
        <span className={`rc-desc-chevron ${open ? 'open' : ''}`}>▶</span>
        Competency Descriptors
      </button>
      {open && (
        <div className="rc-desc-table-wrap">
          {levelDesc.length > 0 && (
            <table className="rc-desc-table">
              <thead>
                <tr>{levelLabels.map(l => <th key={l}>{l}</th>)}</tr>
              </thead>
              <tbody>
                <tr>
                  {[1, 2, 3, 4].map(lv => {
                    const rows = levelDesc.filter(d => d.description_level === lv);
                    return (
                      <td key={lv}>
                        {rows.length > 0
                          ? <ul>{rows.map(r => <li key={r.description_id}>{r.description_name}</li>)}</ul>
                          : <span className="rc-desc-empty">—</span>}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          )}
          {compItems.length > 0 && (
            <ul className="rc-items-list">
              {compItems.map(i => <li key={i.item_id}>{i.description}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════════════
const RoleCompetencyMap = () => {
  const { organizationId } = useAuth();
  const [page, setPage] = useState('landing');

  // ── Lookup tables (fully dynamic from DB) ──────────────────────────────
  const [levelLabels, setLevelLabels] = useState(FALLBACK_LEVEL_LABELS);
  const [relevanceOptions, setRelevanceOptions] = useState(FALLBACK_RELEVANCE_OPTIONS);
  const [performanceOptions, setPerformanceOptions] = useState(FALLBACK_PERF_OPTIONS);

  // ── Selectors ──────────────────────────────────────────────────────────
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);

  // ── View panel state ───────────────────────────────────────────────────
  const [viewTab, setViewTab] = useState('relevance');
  const [viewCompetencies, setViewCompetencies] = useState([]);
  const [viewGroups, setViewGroups] = useState([]);

  // ── Configure page state ───────────────────────────────────────────────
  const [allCompetencyGroups, setAllCompetencyGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [configGroupComps, setConfigGroupComps] = useState([]);
  const [configGroupDescs, setConfigGroupDescs] = useState([]);
  const [configGroupItems, setConfigGroupItems] = useState([]);

  // ── Shared value maps (keyed by competency_id) ─────────────────────────
  const [relevanceValues, setRelevanceValues] = useState({});
  const [performanceValues, setPerformanceValues] = useState({});
  const [relevanceIdMap, setRelevanceIdMap] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [viewRefreshKey, setViewRefreshKey] = useState(0);


  // ══════════════════════════════════════════════════════════════════════════
  // LOOKUP TABLE FETCHES (all fully dynamic — no static fallbacks used after load)
  // ══════════════════════════════════════════════════════════════════════════

  // level_descriptions → level labels for columns
  useEffect(() => {
    supabase
      .from('level_descriptions')
      .select('level_number, title')
      .order('level_number')
      .then(({ data, error }) => {
        if (error) { console.error('level_descriptions:', error); return; }
        const labels = (data || [])
          .sort((a, b) => a.level_number - b.level_number)
          .map(r => r.title);
        if (labels.length === 4) setLevelLabels(labels);
      });
  }, []);

  // relevancelevels → relevance dropdown options (Foundational / 'F' excluded)
  useEffect(() => {
    supabase
      .from('relevancelevels')
      .select('relevance_level, relevance_description')
      .order('relevance_level')
      .then(({ data, error }) => {
        if (error) { console.error('relevancelevels:', error); return; }
        const opts = (data || [])
          .filter(r => r.relevance_level.trim() !== 'F')   // exclude Foundational
          .map(r => {
            const letter = r.relevance_level.trim();
            const canonical = FALLBACK_RELEVANCE_OPTIONS.find(o => o.value === letter);
            return { value: letter, label: canonical?.label || r.relevance_description || letter };
          });
        if (opts.length) setRelevanceOptions(opts);
      });
  }, []);

  // expectedperformancelevels → performance dropdown options
  useEffect(() => {
    supabase
      .from('expectedperformancelevels')
      .select('performance_level, description')
      .order('performance_level')
      .then(({ data, error }) => {
        if (error) { console.error('expectedperformancelevels:', error); return; }
        const opts = (data || []).map(r => ({
          value: r.performance_level,
          label: r.description || String(r.performance_level),
        }));
        if (opts.length) setPerformanceOptions(opts);
      });
  }, []);

  // departments — scoped to this tenant; auto-selects the first entry
  useEffect(() => {
    if (!organizationId) return;
    supabase
      .from('department')
      .select('department_id, depart_name')
      .eq('organization_id', organizationId)
      .order('depart_name')
      .then(({ data, error }) => {
        if (!error) {
          setDepartments(data || []);
          if (data && data.length > 0) {
            setSelectedDepartment(String(data[0].department_id));
          }
        }
      });
  }, [organizationId]);

  // ALL competency groups for configure selector — corp (org-wide) + func (dept-specific)
  useEffect(() => {
    if (!organizationId) return;
    const fetchGroups = async () => {
      const [corpRes, funcRes] = await Promise.all([
        supabase
          .from('corp_competence_groups')
          .select('group_id, group_name, description, weighting')
          .eq('organization_id', organizationId)
          .order('created_at'),
        selectedDepartment
          ? supabase
              .from('func_competence_groups')
              .select('group_id, group_name, description, group_weight')
              .eq('department_id', selectedDepartment)
              .eq('organization_id', organizationId)
              .order('created_at')
          : Promise.resolve({ data: [] }),
      ]);
      const corpGroups = (corpRes.data || []).map(g => ({ ...g, _source: 'corp', group_type: 'Core' }));
      const funcGroups = (funcRes.data || []).map(g => ({ ...g, _source: 'func', group_type: 'Functional' }));
      setAllCompetencyGroups([...corpGroups, ...funcGroups]);
    };
    fetchGroups();
  }, [organizationId, selectedDepartment]);

  // roles by department
  useEffect(() => {
    if (!selectedDepartment) return;
    supabase
      .from('roles')
      .select('role_id, role_title, department_id')
      .eq('department_id', selectedDepartment)
      .order('role_title')
      .then(({ data, error }) => {
        if (error) { console.error('roles:', error); return; }
        setRoles(data || []);
        if (data && !data.find(r => String(r.role_id) === String(selectedRoleId))) {
          setSelectedRoleId(data[0]?.role_id ? String(data[0].role_id) : '');
        }
      });
  }, [selectedDepartment]); // eslint-disable-line

  useEffect(() => {
    setSelectedRole(roles.find(r => String(r.role_id) === String(selectedRoleId)) || null);
  }, [roles, selectedRoleId]);


  // ══════════════════════════════════════════════════════════════════════════
  // VIEW PANEL — competencies via rolecompetencies, resolved via corp/func tables
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedRoleId || !organizationId) {
      setViewCompetencies([]);
      setViewGroups([]);
      return;
    }
    const run = async () => {
      const { data: rcRows, error: rcErr } = await supabase
        .from('rolecompetencies')
        .select('competency_id, source_type')
        .eq('role_id', Number(selectedRoleId))
        .eq('organization_id', organizationId);
      if (rcErr) { console.error('rolecompetencies (view):', rcErr); return; }
      if (!rcRows?.length) { setViewCompetencies([]); setViewGroups([]); return; }

      // treat null source_type (rows saved before the column existed) as 'corp'
      const corpIds = rcRows.filter(r => !r.source_type || r.source_type === 'corp').map(r => r.competency_id);
      const funcIds = rcRows.filter(r => r.source_type === 'func').map(r => r.competency_id);

      const [corpItemsRes, funcItemsRes] = await Promise.all([
        corpIds.length > 0
          ? supabase.from('corp_competence_items').select('competency_id, competency_name, group_id').in('competency_id', corpIds)
          : Promise.resolve({ data: [] }),
        funcIds.length > 0
          ? supabase.from('func_competence_items').select('item_id, item_name, group_id').in('item_id', funcIds)
          : Promise.resolve({ data: [] }),
      ]);

      const allItems = [
        ...(corpItemsRes.data || []).map(c => ({ competency_id: c.competency_id, competency_name: c.competency_name, group_id: c.group_id, _source: 'corp' })),
        ...(funcItemsRes.data || []).map(c => ({ competency_id: c.item_id, competency_name: c.item_name, group_id: c.group_id, _source: 'func' })),
      ];

      const corpGroupIds = [...new Set((corpItemsRes.data || []).map(c => c.group_id))];
      const funcGroupIds = [...new Set((funcItemsRes.data || []).map(c => c.group_id))];

      const [corpGrpRes, funcGrpRes] = await Promise.all([
        corpGroupIds.length > 0
          ? supabase.from('corp_competence_groups').select('group_id, group_name').in('group_id', corpGroupIds)
          : Promise.resolve({ data: [] }),
        funcGroupIds.length > 0
          ? supabase.from('func_competence_groups').select('group_id, group_name').in('group_id', funcGroupIds)
          : Promise.resolve({ data: [] }),
      ]);

      const groupMap = {};
      (corpGrpRes.data || []).forEach(g => { groupMap[`corp-${g.group_id}`] = { ...g, group_type: 'Core', _source: 'corp' }; });
      (funcGrpRes.data || []).forEach(g => { groupMap[`func-${g.group_id}`] = { ...g, group_type: 'Functional', _source: 'func' }; });

      setViewCompetencies(allItems);
      setViewGroups(Object.values(groupMap));
    };
    run();
  }, [selectedRoleId, organizationId, viewRefreshKey]); // eslint-disable-line


  // ══════════════════════════════════════════════════════════════════════════
  // CONFIGURE — competencies from the correct items table based on group source
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedGroupId) {
      setConfigGroupComps([]);
      setConfigGroupDescs([]);
      setConfigGroupItems([]);
      return;
    }
    const group = allCompetencyGroups.find(g => String(g.group_id) === String(selectedGroupId));
    if (!group) return;

    const fetchComps = async () => {
      let comps;
      if (group._source === 'corp') {
        const { data, error } = await supabase
          .from('corp_competence_items')
          .select('competency_id, competency_name')
          .eq('group_id', Number(selectedGroupId))
          .order('created_at');
        if (error) { console.error('corp_competence_items (configure):', error); return; }
        comps = (data || []).map(c => ({
          competency_id: c.competency_id, competency_name: c.competency_name,
          group_id: Number(selectedGroupId), _source: 'corp',
        }));
      } else {
        const { data, error } = await supabase
          .from('func_competence_items')
          .select('item_id, item_name')
          .eq('group_id', Number(selectedGroupId))
          .order('created_at');
        if (error) { console.error('func_competence_items (configure):', error); return; }
        comps = (data || []).map(c => ({
          competency_id: c.item_id, competency_name: c.item_name,
          group_id: Number(selectedGroupId), _source: 'func',
        }));
      }
      setConfigGroupComps(comps);
    };
    fetchComps();
  }, [selectedGroupId, allCompetencyGroups]);

  // Descriptions + items for configure competencies
  useEffect(() => {
    if (!configGroupComps.length) {
      setConfigGroupDescs([]);
      setConfigGroupItems([]);
      return;
    }
    const compIds = configGroupComps.map(c => c.competency_id);

    supabase
      .from('competency_descriptions')
      .select('description_id, competency_id, description_level, description_name, role_id')
      .in('competency_id', compIds)
      .then(({ data, error }) => { if (!error) setConfigGroupDescs(data || []); });

    supabase
      .from('competency_items')
      .select('item_id, competency_id, description, display_order')
      .in('competency_id', compIds)
      .order('display_order', { ascending: true, nullsFirst: false })
      .then(({ data, error }) => { if (!error) setConfigGroupItems(data || []); });
  }, [configGroupComps]);

  // Saved relevance + performance for configure competencies (merged into shared maps)
  useEffect(() => {
    if (!configGroupComps.length || !selectedRoleId) return;
    const compIds = configGroupComps.map(c => c.competency_id);

    supabase
      .from('competency_relevance_levels')
      .select('relevance_id, competency_id, level_1_relevance, level_2_relevance, level_3_relevance, level_4_relevance, level_1_performance, level_2_performance, level_3_performance, level_4_performance')
      .eq('role_id', Number(selectedRoleId))
      .in('competency_id', compIds)
      .then(({ data, error }) => {
        if (error) { console.error('relevance+performance fetch (configure):', error); return; }
        const relMerge = {}, idMerge = {}, perfMerge = {};
        (data || []).forEach(row => {
          relMerge[row.competency_id] = {
            level_1_relevance: row.level_1_relevance || '',
            level_2_relevance: row.level_2_relevance || '',
            level_3_relevance: row.level_3_relevance || '',
            level_4_relevance: row.level_4_relevance || '',
          };
          idMerge[row.competency_id] = row.relevance_id;
          perfMerge[row.competency_id] = {
            lv1: row.level_1_performance ?? '',
            lv2: row.level_2_performance ?? '',
            lv3: row.level_3_performance ?? '',
            lv4: row.level_4_performance ?? '',
          };
        });
        setRelevanceValues(prev => ({ ...prev, ...relMerge }));
        setRelevanceIdMap(prev => ({ ...prev, ...idMerge }));
        setPerformanceValues(prev => ({ ...prev, ...perfMerge }));
      });
  }, [configGroupComps, selectedRoleId]);


  // ══════════════════════════════════════════════════════════════════════════
  // VIEW — saved relevance + performance for view competencies
  // ══════════════════════════════════════════════════════════════════════════
  const fetchViewSavedValues = useCallback(async () => {
    if (!selectedRoleId || !viewCompetencies.length) return;
    const compIds = viewCompetencies.map(c => c.competency_id);

    const { data, error } = await supabase
      .from('competency_relevance_levels')
      .select('relevance_id, competency_id, level_1_relevance, level_2_relevance, level_3_relevance, level_4_relevance, level_1_performance, level_2_performance, level_3_performance, level_4_performance')
      .eq('role_id', Number(selectedRoleId))
      .in('competency_id', compIds);
    if (error) { console.error('relevance+performance fetch (view):', error); return; }

    const relMerge = {}, idMerge = {}, perfMerge = {};
    (data || []).forEach(row => {
      relMerge[row.competency_id] = {
        level_1_relevance: row.level_1_relevance || '',
        level_2_relevance: row.level_2_relevance || '',
        level_3_relevance: row.level_3_relevance || '',
        level_4_relevance: row.level_4_relevance || '',
      };
      idMerge[row.competency_id] = row.relevance_id;
      perfMerge[row.competency_id] = {
        lv1: row.level_1_performance ?? '',
        lv2: row.level_2_performance ?? '',
        lv3: row.level_3_performance ?? '',
        lv4: row.level_4_performance ?? '',
      };
    });
    setRelevanceValues(prev => ({ ...prev, ...relMerge }));
    setRelevanceIdMap(prev => ({ ...prev, ...idMerge }));
    setPerformanceValues(prev => ({ ...prev, ...perfMerge }));
  }, [selectedRoleId, viewCompetencies]);

  useEffect(() => { fetchViewSavedValues(); }, [fetchViewSavedValues]);


  // ══════════════════════════════════════════════════════════════════════════
  // VALUE CHANGE HANDLERS
  // ══════════════════════════════════════════════════════════════════════════
  const handleRelevanceChange = (compId, level, value) => {
    setRelevanceValues(prev => ({
      ...prev,
      [compId]: { ...(prev[compId] || emptyRelRow()), [relKey(level)]: value },
    }));
  };

  const handlePerformanceChange = (compId, level, value) => {
    setPerformanceValues(prev => ({
      ...prev,
      [compId]: {
        ...(prev[compId] || emptyPerfRow()),
        [`lv${level}`]: value === '' ? '' : Number(value),
      },
    }));
  };


  // ══════════════════════════════════════════════════════════════════════════
  // PER-COMPETENCY: does it have at least one relevance level set?
  // Performance unlocks as soon as the user fills in ANY relevance level
  // for that individual competency.
  // ══════════════════════════════════════════════════════════════════════════
  const compHasRelevance = useCallback((compId) => {
    const r = relevanceValues[compId];
    return r && [1, 2, 3, 4].some(lv => r[relKey(lv)]);
  }, [relevanceValues]);


  // ══════════════════════════════════════════════════════════════════════════
  // CONFIGURE PROGRESS
  // Counts how many competencies have full relevance / full performance set.
  // ══════════════════════════════════════════════════════════════════════════
  const configProgress = useMemo(() => {
    const total = configGroupComps.length;
    if (!total) return { pct: 0, relDone: 0, perfDone: 0, total: 0 };
    let relDone = 0, perfDone = 0;
    configGroupComps.forEach(c => {
      const r = relevanceValues[c.competency_id];
      if (r && [1, 2, 3, 4].every(lv => r[relKey(lv)])) relDone++;
      const p = performanceValues[c.competency_id];
      if (p && ['lv1', 'lv2', 'lv3', 'lv4'].every(k => p[k] !== '' && p[k] !== undefined)) perfDone++;
    });
    const pct = Math.round(((relDone + perfDone) / (total * 2)) * 100);
    return { pct, relDone, perfDone, total };
  }, [configGroupComps, relevanceValues, performanceValues]);


  // ══════════════════════════════════════════════════════════════════════════
  // SAVE
  // ══════════════════════════════════════════════════════════════════════════
  const handleSave = async () => {
    if (!selectedGroupId || !configGroupComps.length) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const compsWithRelevance = configGroupComps.filter(comp => {
        const rv = relevanceValues[comp.competency_id];
        return rv && [1, 2, 3, 4].some(lv => rv[relKey(lv)]);
      });

      // 1. Upsert rolecompetencies — include source_type so the view can resolve corp vs func
      if (compsWithRelevance.length) {
        const group = allCompetencyGroups.find(g => String(g.group_id) === String(selectedGroupId));
        const rcRows = compsWithRelevance.map(comp => ({
          role_id:         Number(selectedRoleId),
          competency_id:   comp.competency_id,
          organization_id: organizationId,
          source_type:     comp._source || group?._source || 'corp',
        }));
        const { error: rcErr } = await supabase
          .from('rolecompetencies')
          .upsert(rcRows, { onConflict: 'role_id,competency_id', ignoreDuplicates: true });
        if (rcErr) throw rcErr;
      }

      // 2. Upsert competency_relevance_levels — includes both relevance AND performance
      //    per-level so saves are atomic and no PK-collision issues arise.
      const toNullableInt = v => (v !== '' && v !== undefined && v !== null) ? Number(v) : null;

      const relRows = compsWithRelevance.map(comp => {
        const rv = relevanceValues[comp.competency_id] || emptyRelRow();
        const pv = performanceValues[comp.competency_id] || emptyPerfRow();
        const row = {
          role_id:             Number(selectedRoleId),
          competency_id:       comp.competency_id,
          organization_id:     organizationId,
          level_1_relevance:   rv.level_1_relevance || null,
          level_2_relevance:   rv.level_2_relevance || null,
          level_3_relevance:   rv.level_3_relevance || null,
          level_4_relevance:   rv.level_4_relevance || null,
          level_1_performance: toNullableInt(pv.lv1),
          level_2_performance: toNullableInt(pv.lv2),
          level_3_performance: toNullableInt(pv.lv3),
          level_4_performance: toNullableInt(pv.lv4),
        };
        if (relevanceIdMap[comp.competency_id]) row.relevance_id = relevanceIdMap[comp.competency_id];
        return row;
      });

      if (relRows.length) {
        const { data: savedRel, error: relErr } = await supabase
          .from('competency_relevance_levels')
          .upsert(relRows, { onConflict: 'role_id,competency_id' })
          .select('relevance_id, competency_id');
        if (relErr) throw relErr;
        const newIdMap = { ...relevanceIdMap };
        (savedRel || []).forEach(r => { newIdMap[r.competency_id] = r.relevance_id; });
        setRelevanceIdMap(newIdMap);
      }

      setSaveMsg('Saved successfully!');
      // Refresh view data and return to landing so the user sees the updated map.
      setViewRefreshKey(k => k + 1);
      setTimeout(() => {
        setSaveMsg('');
        setPage('landing');
      }, 1500);
    } catch (e) {
      console.error('Save error:', e);
      setSaveMsg('Error saving. Please try again.');
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 4000);
      return;
    }
    setSaving(false);
  };


  // ══════════════════════════════════════════════════════════════════════════
  // VIEW TABLE DATA
  // ══════════════════════════════════════════════════════════════════════════
  const viewTableData = useMemo(() => {
    return viewCompetencies.map(comp => {
      const key = `${comp._source}-${comp.group_id}`;
      const group = viewGroups.find(g => `${g._source}-${g.group_id}` === key) || viewGroups.find(g => g.group_id === comp.group_id);
      const row = { competency: comp.competency_name, groupName: group?.group_name || '', competency_id: comp.competency_id };
      [1, 2, 3, 4].forEach(lv => {
        row[`level${lv}`] = viewTab === 'relevance'
          ? (relevanceValues[comp.competency_id]?.[relKey(lv)] || '')
          : (performanceValues[comp.competency_id]?.[`lv${lv}`] ?? '');
      });
      return row;
    });
  }, [viewCompetencies, viewGroups, viewTab, relevanceValues, performanceValues]);

  const hasAnyRelevance = useMemo(() =>
    viewCompetencies.some(c => {
      const r = relevanceValues[c.competency_id];
      return r && [1, 2, 3, 4].some(lv => r[relKey(lv)]);
    }),
    [viewCompetencies, relevanceValues]
  );

  const getRelLabel = (val) => relevanceOptions.find(o => o.value === val)?.label || '—';
  const getPerfLabel = (val) => performanceOptions.find(o => String(o.value) === String(val))?.label || '—';

  const handleDownloadReport = () => {
    if (!selectedRole || !viewCompetencies.length) return;

    // eslint-disable-next-line new-cap
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const PW = 297;
    const PH = 210;
    const ML = 14;
    const MR = 14;

    const GREEN = [0, 107, 84];
    const WHITE = [255, 255, 255];
    const DARK  = [31, 41, 55];
    const LGRAY = [229, 231, 235];
    const BG    = [248, 249, 250];

    const printDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const deptName  = departments.find(d => String(d.department_id) === String(selectedDepartment))?.depart_name || '';

    const sf = (...rgb) => doc.setFillColor(...rgb);
    const st = (...rgb) => doc.setTextColor(...rgb);

    const drawPageHeader = () => {
      sf(...GREEN);
      doc.rect(0, 0, PW, 26, 'F');
      sf(0, 139, 109);
      doc.rect(0, 26, PW, 1.5, 'F');
      st(...WHITE);
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('TalentScope360', ML, 11);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.text('Competency Mapping Report', ML, 20);
      doc.setFontSize(7.5);
      doc.text(`Generated: ${printDate}`, PW - MR, 20, { align: 'right' });
    };

    const drawSectionLabel = (title, yPos) => {
      sf(...GREEN);
      doc.rect(ML, yPos, PW - ML - MR, 0.5, 'F');
      st(...GREEN);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(title.toUpperCase(), ML, yPos + 8);
      return yPos + 12;
    };

    drawPageHeader();

    // Role info card
    const cardY = 32;
    sf(...BG);
    doc.setDrawColor(...LGRAY);
    doc.setLineWidth(0.3);
    doc.roundedRect(ML, cardY, PW - ML - MR, 18, 3, 3, 'FD');
    st(...DARK);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(selectedRole.role_title, ML + 6, cardY + 7);
    st(107, 114, 128);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`Department: ${deptName}`, ML + 6, cardY + 14);

    // Build grouped data (same logic as landing page groupedView)
    const pdfGrouped = viewGroups.map(g => ({
      group: g,
      rows: viewTableData.filter(r => r.groupName === g.group_name),
    }));

    // ── Relevance table ────────────────────────────────────────────────────
    let y = drawSectionLabel('Competency Relevance Map', cardY + 22);

    const relHead = [['Group', 'Competency', ...levelLabels]];
    const relBody = [];
    pdfGrouped.forEach(({ group, rows }) => {
      rows.forEach((row, idx) => {
        relBody.push([
          idx === 0 ? group.group_name : '',
          row.competency,
          getRelLabel(relevanceValues[row.competency_id]?.[relKey(1)] || ''),
          getRelLabel(relevanceValues[row.competency_id]?.[relKey(2)] || ''),
          getRelLabel(relevanceValues[row.competency_id]?.[relKey(3)] || ''),
          getRelLabel(relevanceValues[row.competency_id]?.[relKey(4)] || ''),
        ]);
      });
    });

    doc.autoTable({
      head: relHead,
      body: relBody,
      startY: y,
      margin: { left: ML, right: MR, bottom: 15 },
      headStyles: { fillColor: GREEN, textColor: WHITE, fontSize: 8.5, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: DARK },
      alternateRowStyles: { fillColor: [248, 252, 250] },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'bold', textColor: [0, 107, 84] },
        1: { cellWidth: 65 },
      },
      theme: 'grid',
      didDrawPage: () => drawPageHeader(),
    });

    // ── Performance table ──────────────────────────────────────────────────
    let perfY = doc.lastAutoTable.finalY + 12;
    if (perfY > PH - 60) {
      doc.addPage();
      drawPageHeader();
      perfY = 36;
    }

    perfY = drawSectionLabel('Expected Performance Map', perfY);

    const perfHead = [['Group', 'Competency', ...levelLabels]];
    const perfBody = [];
    pdfGrouped.forEach(({ group, rows }) => {
      rows.forEach((row, idx) => {
        perfBody.push([
          idx === 0 ? group.group_name : '',
          row.competency,
          getPerfLabel(performanceValues[row.competency_id]?.lv1 ?? ''),
          getPerfLabel(performanceValues[row.competency_id]?.lv2 ?? ''),
          getPerfLabel(performanceValues[row.competency_id]?.lv3 ?? ''),
          getPerfLabel(performanceValues[row.competency_id]?.lv4 ?? ''),
        ]);
      });
    });

    doc.autoTable({
      head: perfHead,
      body: perfBody,
      startY: perfY,
      margin: { left: ML, right: MR, bottom: 15 },
      headStyles: { fillColor: GREEN, textColor: WHITE, fontSize: 8.5, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: DARK },
      alternateRowStyles: { fillColor: [248, 252, 250] },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'bold', textColor: [0, 107, 84] },
        1: { cellWidth: 65 },
      },
      theme: 'grid',
      didDrawPage: () => drawPageHeader(),
    });

    // Footers on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      sf(...GREEN);
      doc.rect(0, PH - 10, PW, 10, 'F');
      st(...WHITE);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text('TalentScope360  ·  Competency Mapping Report  ·  Confidential', ML, PH - 4);
      doc.text(`Page ${i} of ${pageCount}`, PW - MR, PH - 4, { align: 'right' });
    }

    const filename = `competency-map_${selectedRole.role_title.replace(/\s+/g, '-').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  // Shared role selector component
  const RoleSelector = () => (
    <div className="rc-selectors">
      <div className="rc-selector-group">
        <label>Department</label>
        <select value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)}>
          <option value="">Select department</option>
          {departments.map(d => (
            <option key={d.department_id} value={d.department_id}>{d.depart_name}</option>
          ))}
        </select>
      </div>
      <div className="rc-selector-group">
        <label>Role</label>
        <select value={selectedRoleId} onChange={e => setSelectedRoleId(e.target.value)}>
          <option value="">Select role</option>
          {roles.map(r => (
            <option key={r.role_id} value={r.role_id}>{r.role_title}</option>
          ))}
        </select>
      </div>
    </div>
  );


  // ══════════════════════════════════════════════════════════════════════════
  // LANDING PAGE
  // ══════════════════════════════════════════════════════════════════════════
  if (page === 'landing') {
    const groupedView = viewGroups.map(g => ({
      group: g,
      rows: viewTableData.filter(r => r.groupName === g.group_name),
    }));

    return (
      <div className="rc-root">
        <div className="rc-landing-header">
          <h1 className="rc-main-title"><i className="bi bi-person" />Competency Maps</h1>
          <p className="rc-sub">
            Define relevance and expected performance levels for each competency across roles.
          </p>
        </div>

        <div className="rc-landing-controls">
          <RoleSelector />
          {selectedRoleId && (
            <div className="rc-landing-actions">
              {viewCompetencies.length > 0 && hasAnyRelevance && (
                <button
                  className="rc-btn rc-btn-outline"
                  onClick={handleDownloadReport}
                >
                  <i className="bi bi-download" /> Download Report
                </button>
              )}
              <button
                className="rc-btn rc-btn-primary"
                onClick={() => { setSelectedGroupId(''); setPage('configure'); }}
              >
                <span className="rc-btn-icon">⚙</span> Configure
              </button>
            </div>
          )}
        </div>

        {!selectedRoleId && (
          <div className="rc-empty rc-empty-prompt">
            Select a department and role above to view the competency map.
          </div>
        )}

        {selectedRole && viewCompetencies.length === 0 && (
          <div className="rc-empty">
            Competencies have not been configured for <strong>{selectedRole.role_title}</strong> yet.
            Click <em>Configure</em> to set up competency relevance and expected performance.
          </div>
        )}

        {selectedRole && viewCompetencies.length > 0 && !hasAnyRelevance && (
          <div className="rc-notice">
            Competency relevance and expected performance levels have not been configured yet
            for <strong>{selectedRole.role_title}</strong>. Click <em>Configure</em> to get started.
          </div>
        )}

        {selectedRole && viewCompetencies.length > 0 && (
          <>
            <div className="rc-tab-strip">
              {['relevance', 'performance'].map(t => (
                <button
                  key={t}
                  className={`rc-tab ${viewTab === t ? 'rc-tab-active' : ''}`}
                  onClick={() => setViewTab(t)}
                >
                  {t === 'relevance' ? 'Relevance' : 'Expected Performance'}
                </button>
              ))}
            </div>

            <div className="rc-view-content">
              {groupedView.map(({ group, rows }) => (
                <div key={group.group_id} className="rc-view-group">
                  <div className="rc-view-group-title">{group.group_name}</div>
                  <table className="rc-view-table">
                    <thead>
                      <tr>
                        <th>Competency</th>
                        {levelLabels.map(l => <th key={l}>{l}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => (
                        <tr key={row.competency_id}>
                          <td className="rc-comp-name">{row.competency}</td>
                          {[1, 2, 3, 4].map(lv => {
                            const val = row[`level${lv}`];
                            const label = viewTab === 'relevance' ? getRelLabel(val) : getPerfLabel(val);
                            return (
                              <td key={lv}>
                                <span className={`rc-view-chip ${val ? 'rc-chip-set' : 'rc-chip-empty'}`}>
                                  {val ? label : '—'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }


  // ══════════════════════════════════════════════════════════════════════════
  // CONFIGURE PAGE
  // ══════════════════════════════════════════════════════════════════════════
  if (page === 'configure') {
    const selectedGroup = allCompetencyGroups.find(g => String(g.group_id) === String(selectedGroupId));

    return (
      <div className="rc-root">
        {/* ── Centered header ── */}
        <div className="rc-landing-header">
          <h1 className="rc-main-title"><i className="bi bi-person" />Competency Maps</h1>
          <p className="rc-config-role-label">
            Configuring: <strong>{selectedRole?.role_title}</strong>
          </p>
        </div>

        {/* ── Group selector ── */}
        <div className="rc-configure-controls">
          <div className="rc-selector-group">
            <label>Competency Group</label>
            <select value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)}>
              <option value="">— Select a group —</option>
              {allCompetencyGroups.map(g => (
                <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Progress bar (visible once a group is selected) ── */}
        {selectedGroupId && configGroupComps.length > 0 && (
          <div className="rc-progress-wrap">
            <div className="rc-progress-header">
              <span className="rc-progress-title">
                Setup Progress — <em>{selectedGroup?.group_name}</em>
              </span>
              <span className="rc-progress-stats">
                {configProgress.relDone}/{configProgress.total} relevance &nbsp;·&nbsp;
                {configProgress.perfDone}/{configProgress.total} performance
                &nbsp;·&nbsp;<strong>{configProgress.pct}%</strong> complete
              </span>
            </div>
            <div className="rc-progress-track">
              <div
                className="rc-progress-fill"
                style={{ width: `${configProgress.pct}%` }}
              />
            </div>
            <div className="rc-progress-steps">
              <span>
                <span className={`rc-step-dot ${configProgress.relDone > 0 ? 'active' : ''}`} />
                Relevance
              </span>
              <span>
                <span className={`rc-step-dot ${configProgress.perfDone > 0 ? 'active' : ''}`} />
                Expected Performance
              </span>
            </div>
          </div>
        )}

        {/* ── Prompt when no group selected ── */}
        {!selectedGroupId && (
          <div className="rc-empty rc-empty-prompt">
            Select a competency group above to configure its competencies for this role.
          </div>
        )}

        {/* ── No competencies in this group ── */}
        {selectedGroupId && configGroupComps.length === 0 && (
          <div className="rc-empty">
            No competencies found under <strong>{selectedGroup?.group_name}</strong>.
          </div>
        )}

        {/* ── Competency cards ── */}
        {selectedGroupId && configGroupComps.length > 0 && (
          <div className="rc-configure-body">
            {configGroupComps.map((comp, idx) => {
              const relVals  = relevanceValues[comp.competency_id]  || emptyRelRow();
              const perfVals = performanceValues[comp.competency_id] || emptyPerfRow();
              const showPerf = compHasRelevance(comp.competency_id);

              return (
                <div key={comp.competency_id} className="rc-comp-card">
                  {/* Card header */}
                  <div className="rc-comp-card-header">
                    <div className="rc-comp-card-meta">
                      {selectedGroup && <span className="rc-comp-badge">{selectedGroup.group_name}</span>}
                      <span className="rc-comp-index">#{idx + 1}</span>
                      <span className="rc-comp-card-title">{comp.competency_name}</span>
                    </div>
                  </div>

                  {/* Descriptor accordion */}
                  <DescriptorAccordion
                    competencyId={comp.competency_id}
                    descriptions={configGroupDescs}
                    items={configGroupItems}
                    levelLabels={levelLabels}
                  />

                  {/* ── Relevance section ── */}
                  <div className="rc-config-section">
                    <div className="rc-config-section-header">
                      <span className="rc-config-section-label">Relevance</span>
                      <span className="rc-config-section-hint">
                        How important is this competency at each career level?
                      </span>
                    </div>
                    <div className="rc-level-grid">
                      {[1, 2, 3, 4].map(lv => (
                        <div key={lv} className="rc-level-col">
                          <label className="rc-level-label">{levelLabels[lv - 1]}</label>
                          <select
                            className="rc-level-select"
                            value={relVals[relKey(lv)] ?? ''}
                            onChange={e => handleRelevanceChange(comp.competency_id, lv, e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {relevanceOptions.map((o, i) => (
                              <option key={o.value} value={o.value}>{i + 1}. {o.label}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Expected Performance section ── */}
                  {/* Unlocks as soon as this competency has ANY relevance level set */}
                  {showPerf && (
                    <div className="rc-config-section">
                      <div className="rc-config-section-header">
                        <span className="rc-config-section-label">Expected Performance</span>
                        <span className="rc-config-section-hint">
                          What performance is expected at each career level?
                        </span>
                      </div>
                      <div className="rc-level-grid">
                        {[1, 2, 3, 4].map(lv => (
                          <div key={lv} className="rc-level-col">
                            <label className="rc-level-label">{levelLabels[lv - 1]}</label>
                            <select
                              className="rc-level-select"
                              value={perfVals[`lv${lv}`] ?? ''}
                              onChange={e => handlePerformanceChange(comp.competency_id, lv, e.target.value)}
                            >
                              <option value="">— Select —</option>
                              {performanceOptions.map((o, i) => (
                                <option key={o.value} value={o.value}>{i + 1}. {o.label}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* ── Page-level actions: Back far-left · Save far-right ── */}
            <div className="rc-page-actions">
              <button
                className="rc-btn rc-btn-outline"
                onClick={() => setPage('landing')}
              >
                ← Back
              </button>
              <div className="rc-page-actions-right">
                {saveMsg && (
                  <span className={`rc-save-msg ${saveMsg.includes('Error') ? 'rc-save-err' : 'rc-save-ok'}`}>
                    {saveMsg}
                  </span>
                )}
                <button
                  className="rc-btn rc-btn-primary"
                  onClick={handleSave}
                  disabled={saving || !selectedGroupId || configGroupComps.length === 0}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default RoleCompetencyMap;
